import Immutable from 'immutable'

import * as boxPricesApi from 'apis/boxPrices'
import menuConfig from 'config/menu'
import * as orderV2 from 'routes/Menu/apis/orderV2'
import { getBasketOrderId, getBasketTariffId, getPromoCode } from 'selectors/basket'
import { locationQuery } from 'selectors/routing'
import GoustoException from 'utils/GoustoException'
import { limitReached } from 'utils/basket'
import logger from 'utils/logger'

import { availablePortions } from '../config/portionSizes'
import { actionTypes } from './actionTypes'
import {
  basketChosenAddressChange,
  basketDateChange,
  basketIdChange,
  basketNumPortionChange,
  basketOrderLoaded,
  basketPostcodeChange,
  basketProductAdd,
  basketReset,
  basketSlotChange,
} from './basket'
import {
  loadMenuCollectionsWithMenuService,
  mapRecipesAvailabilityToStock,
} from './menuActionHelper'
import { productsLoadCategories, productsLoadProductsById, productsLoadStock } from './products'
import statusActions from './status'
import tempActions from './temp'
import * as trackingKeys from './trackingKeys'

export function menuReceiveBoxPrices(prices, tariffId) {
  return {
    type: actionTypes.MENU_BOX_PRICES_RECEIVE,
    prices,
    tariffId,
  }
}

export function menuChangeRecipeStock(stock) {
  return {
    type: actionTypes.MENU_RECIPE_STOCK_CHANGE,
    stock,
  }
}

export function menuReplaceRecipeStock(stock) {
  return {
    type: actionTypes.MENU_RECIPE_STOCK_REPLACE,
    stock,
  }
}

export function menuReceiveMenuPending(pending) {
  return {
    type: actionTypes.MENU_RECIPES_RECEIVE_PENDING,
    pending,
  }
}

export function findSlot(deliveryDays, coreSlotId) {
  let slotId
  deliveryDays.some((deliveryDay) => {
    const matchedSlot = deliveryDay.get('slots').find((slot) => {
      if (String(slot.get('coreSlotId')) === String(coreSlotId)) {
        return true
      }

      return false
    })

    if (matchedSlot) {
      slotId = matchedSlot.get('id')

      return true
    }

    return false
  })

  return slotId
}

export function menuCutoffUntilReceive(cutoffUntil) {
  return {
    type: actionTypes.MENU_CUTOFF_UNTIL_RECEIVE,
    cutoffUntil,
  }
}

export function menuLoadMenu() {
  return async (dispatch, getState) => {
    const state = getState()

    dispatch(menuActions.menuReceiveMenuPending(true))

    await loadMenuCollectionsWithMenuService(dispatch, getState)

    dispatch(menuActions.menuReceiveMenuPending(false))

    const reachedLimit = limitReached(state.basket, state.menuRecipes, state.menuRecipeStock)
    dispatch({
      type: actionTypes.BASKET_LIMIT_REACHED,
      limitReached: reachedLimit,
    })
  }
}

export function menuLoadBoxPrices() {
  return async (dispatch, getState) => {
    try {
      const state = getState()
      const promoCode = getPromoCode(state)
      const orderId = getBasketOrderId(state)
      const tariffId = getBasketTariffId(state)
      const reqData = {}

      if (orderId) {
        reqData.order_id = orderId
      } else if (promoCode) {
        reqData.promocode = promoCode
      }

      if (!getState().auth.get('isAuthenticated') && tariffId) {
        reqData.tariff_id = tariffId
      }

      dispatch(statusActions.pending(actionTypes.MENU_BOX_PRICES_RECEIVE, true))
      dispatch(statusActions.error(actionTypes.MENU_BOX_PRICES_RECEIVE, false))

      try {
        const { data: recipePrices } = await boxPricesApi.fetchBoxPrices(
          getState().auth.get('accessToken'),
          reqData,
        )
        dispatch(menuActions.menuReceiveBoxPrices(recipePrices, tariffId))
      } catch (err) {
        dispatch(menuActions.menuReceiveBoxPrices({}))

        throw new GoustoException(
          `Could not load menu box prices: fetch failed${
            tariffId ? ` for tariff_id "${tariffId}"` : ''
          }, ${err}`,
          {
            error: 'fetch-failed',
          },
        )
      }
    } catch (err) {
      const errMessage = err.message || err
      logger[err.level || 'error'](errMessage)
      dispatch(statusActions.error(actionTypes.MENU_BOX_PRICES_RECEIVE, err.error || errMessage))
    } finally {
      dispatch(statusActions.pending(actionTypes.MENU_BOX_PRICES_RECEIVE, false))
    }
  }
}

export function menuLoadOrderDetails(orderId, addRecipe) {
  return async (dispatch, getState) => {
    try {
      await dispatch(statusActions.pending(actionTypes.LOADING_ORDER, true))

      const { data: order } = await orderV2.fetchOrder(
        dispatch,
        getState,
        orderId,
        'shipping_address',
      )
      dispatch(basketReset())
      dispatch(basketDateChange(order.deliveryDate))
      dispatch(basketNumPortionChange(order.box.numPortions, orderId))

      order.recipeItems.forEach((recipe) => {
        const qty = Math.round(parseInt(recipe.quantity, 10) / parseInt(order.box.numPortions, 10))

        const adjustedQty = menuConfig.stockThreshold + qty
        dispatch(
          menuActions.menuChangeRecipeStock({
            [recipe.recipeId]: { [order.box.numPortions]: adjustedQty },
          }),
        )

        for (let i = 1; i <= qty; i++) {
          // fall back to the defaults for these 3 params
          const view = undefined
          const recipeInfo = undefined
          const maxRecipesNum = undefined

          addRecipe(recipe.recipeId, view, recipeInfo, maxRecipesNum, orderId)
        }
      })

      const productItems = order.productItems || []
      if (productItems.length) {
        const productItemIds = productItems.map((productItem) => productItem.itemableId)
        await dispatch(productsLoadProductsById(productItemIds))
        await dispatch(productsLoadStock())
        await dispatch(productsLoadCategories())
        productItems.forEach((product) => {
          for (let i = 0; i < parseInt(product.quantity, 10); i++) {
            dispatch(basketProductAdd(product.itemableId))
          }
        })
      }

      dispatch(basketIdChange(order.id))
      dispatch(basketOrderLoaded(order.id))
      dispatch(basketChosenAddressChange(order.shippingAddress))

      const grossTotal = order && order.prices && order.prices.grossTotal
      const netTotal = order && order.prices && order.prices.total

      dispatch(tempActions.temp('originalGrossTotal', grossTotal))
      dispatch(tempActions.temp('originalNetTotal', netTotal))

      // This sets `boxSummaryDeliveryDays`
      await dispatch(basketPostcodeChange(order.shippingAddress.postcode))

      const newState = getState()
      const coreSlotId = order.deliverySlot.id
      const slotId = findSlot(newState.boxSummaryDeliveryDays, coreSlotId)
      dispatch(basketSlotChange(slotId))
    } finally {
      dispatch(statusActions.pending(actionTypes.LOADING_ORDER, false))
    }
  }
}

export function menuClearStock() {
  return {
    type: actionTypes.MENU_RECIPE_STOCK_CLEAR,
  }
}

export function menuLoadStock(clearStock = true, isMenuApiStockEnabled = false) {
  return async (dispatch, getState) => {
    const { basket, boxSummaryDeliveryDays } = getState()
    const date = basket.get('date')
    const coreDayId = boxSummaryDeliveryDays.getIn([date, 'coreDayId'], '')

    if (!coreDayId) {
      return
    }

    const query = locationQuery(getState())
    const isAdmin = query && query['preview[menu_id]']

    const recipeIdMap = Object.values(getState().menuService?.recipe).reduce(
      (acc, recipe) => ({
        [recipe.id]: parseInt(recipe.attributes.core_recipe_id, 10),
        ...acc,
      }),
      {},
    )

    // set recipeAvailability state from recipe portion availability data in menuservice
    let recipeAvailability = {}
    if (!isMenuApiStockEnabled) {
      recipeAvailability = getState().recipeAvailability
    } else {
      const deliveryDate = getState().basket.get('date')
      const menuServiceRecipeAvailability =
        getState().menuService.data[0].relationships.recipes.data

      recipeAvailability = menuServiceRecipeAvailability.reduce((acc, recipe) => {
        const portionSizesAvaiableForRecipe = recipe.meta.portion_sizes_available

        acc[recipe.id] = {
          recipeId: recipe.id,
          deliveryDate,
          portions: availablePortions.reduce(
            (portionsAcc, currentValue) => ({
              ...portionsAcc,
              [currentValue]: { available: portionSizesAvaiableForRecipe.includes(currentValue) },
            }),
            {},
          ),
        }

        return acc
      }, {})
    }

    const mappedRecipeAvailabilityToCoreId = Object.values(recipeAvailability).map(
      (recipeAvailabilityRecipe) => ({
        ...recipeAvailabilityRecipe,
        recipeId: recipeIdMap[recipeAvailabilityRecipe.recipeId],
      }),
    )
    const stockData = mapRecipesAvailabilityToStock(mappedRecipeAvailabilityToCoreId, isAdmin)

    const recipeStockChangeAction = clearStock ? menuReplaceRecipeStock : menuChangeRecipeStock
    dispatch(recipeStockChangeAction(stockData))

    if (!clearStock) {
      return
    }

    const numPortions = getState().basket.get('numPortions')

    const recipes = getState().basket.get('recipes', Immutable.Map({}))
    recipes.forEach((amount, recipeId) => {
      for (let x = 0; x < amount; x++) {
        dispatch(menuChangeRecipeStock({ [recipeId]: { [numPortions]: -1 } }))
      }
    })
  }
}

export function menuBrowseCTAVisibilityChange(show) {
  const trackingData = {
    actionType: trackingKeys.showBrowseCta,
    show,
  }

  return {
    type: actionTypes.MENU_BROWSE_CTA_VISIBILITY_CHANGE,
    show,
    ...(show ? { trackingData } : {}),
  }
}

export const menuLoadComplete = (timeToLoadMs, useMenuService) => ({
  type: actionTypes.MENU_LOAD_COMPLETE,
  timeToLoadMs,
  useMenuService,
})

export const trackVariantListDisplay = (view) => ({
  type: actionTypes.TRACK_VARIANT_RECIPE_LIST_DISPLAY,
  trackingData: {
    actionType: trackingKeys.recipeVariantActionSheet,
    view,
  },
})

export const sideEventScreens = {
  orderSidesScreen: 'order_sides_screen',
  orderSidesAllergensScreen: 'order_sides_allergens_screen',
}

export const sideEventTypes = {
  primaryAction: 'primary_action',
  screenView: 'screen_view',
  closeScreen: 'close_screen',
  tertiaryAction: 'tertiary_action',
}

export const trackSidesContinueClicked = (sidesIds, sidesTotalSurcharge, totalNumberOfSides) => ({
  type: actionTypes.TRACK_CONTINUE_WITH_SIDES_CLICKED,
  trackingData: {
    event_name: trackingKeys.sideModalSidesContinueClicked,
    event_screen: sideEventScreens.orderSidesScreen,
    event_type: sideEventTypes.primaryAction,
    sides_ids: sidesIds,
    sides_total_surcharge: sidesTotalSurcharge,
    sides_counts: totalNumberOfSides,
  },
})

export const trackViewSidesModal = () => ({
  type: actionTypes.TRACK_VIEW_ORDER_SIDES_SCREEN,
  trackingData: {
    event_name: trackingKeys.sideModalViewOrderSidesScreen,
    event_screen: sideEventScreens.orderSidesScreen,
    event_type: sideEventTypes.screenView,
  },
})

export const trackCancelSide = () => ({
  type: actionTypes.TRACK_ORDER_SIDES_CANCEL,
  trackingData: {
    event_name: trackingKeys.sideModalOrderSidesCancel,
    event_type: sideEventTypes.closeScreen,
  },
})

export const trackAddSide = (sideId, orderId) => ({
  type: actionTypes.TRACK_ADD_SIDE,
  trackingData: {
    event_name: trackingKeys.sideModalAddSide,
    side_id: sideId,
    order_id: orderId,
  },
})

export const trackViewSidesAllergens = () => ({
  type: actionTypes.TRACK_VIEW_ORDER_SIDES_ALLERGENS_SCREEN,
  trackingData: {
    event_name: trackingKeys.sideModalViewOrderSidesAllergensScreen,
    event_screen: sideEventScreens.orderSidesScreen,
    event_type: sideEventTypes.tertiaryAction,
  },
})

export const trackCloseSidesAllergens = () => ({
  type: actionTypes.TRACK_CLOSE_ORDER_SIDES_ALLERGENS_SCREEN,
  trackingData: {
    event_name: trackingKeys.sideModalClose,
    event_screen: sideEventScreens.orderSidesAllergensScreen,
    event_type: sideEventTypes.closeScreen,
  },
})

/**
 * menuActions should only be used for the tests
 * to mock methods and internal to call other methods
 * defined. Please don't use outside.
 */
/* eslint-disable no-use-before-define */
const menuActions = {
  menuLoadMenu,
  menuLoadBoxPrices,
  menuLoadOrderDetails,
  menuCutoffUntilReceive,
  menuClearStock,
  menuLoadStock,
  menuBrowseCTAVisibilityChange,
  menuReceiveMenuPending,
  menuReceiveBoxPrices,
  trackViewSidesModal,
  trackSidesContinueClicked,
  trackAddSide,
  trackCancelSide,
  trackViewSidesAllergens,
  trackCloseSidesAllergens,
  menuChangeRecipeStock,
}

export default menuActions
